import React, { useState, useEffect, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { functionsBaseUrl } from '@/config/functionsUrl';
import { useTranslation } from 'react-i18next';
import { AppState, AppAction } from '@/types';
import { showToast } from '@/components/ui/Toast';
import { PawPrint, Plus, X, ChevronRight } from 'lucide-react';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { PetProfile, ExtendedPet } from './PetProfile';

// ── Types ──────────────────────────────────────────────────────────────────────

const SPECIES_ICONS: Record<string, string> = {
  dog: '🐕', cat: '🐱', bird: '🐦', reptile: '🦎', other: '🐾',
  DOG: '🐕', CAT: '🐱', BIRD: '🐦', REPTILE: '🦎', OTHER: '🐾',
};

async function apiFetch(path: string, method: string, body?: object): Promise<any> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  const res = await fetch(`${functionsBaseUrl}/apiPets${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error ?? 'Request failed');
  }
  if (res.status === 204) return null;
  return res.json();
}

interface PetsProps {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

// ── Pets list view ─────────────────────────────────────────────────────────────

export const Pets: React.FC<PetsProps> = ({ state }) => {
  const { t } = useTranslation();
  const householdId = state.currentHouseholdId;
  const [pets, setPets] = useState<ExtendedPet[]>([]);
  const [selectedPet, setSelectedPet] = useState<ExtendedPet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add pet modal
  const [showAddPet, setShowAddPet] = useState(false);
  const [newPetName, setNewPetName] = useState('');
  const [newPetSpecies, setNewPetSpecies] = useState('dog');
  const [newPetBreed, setNewPetBreed] = useState('');
  const [newPetAge, setNewPetAge] = useState('');
  const [saving, setSaving] = useState(false);

  const loadPets = useCallback(async () => {
    if (!householdId) return;
    try {
      setLoading(true);
      const res = await apiFetch(`?householdId=${householdId}`, 'GET');
      setPets(res.data ?? []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [householdId]);

  useEffect(() => { loadPets(); }, [loadPets]);

  const handleAddPet = async () => {
    if (!householdId || !newPetName.trim()) return;
    setSaving(true);
    try {
      const res = await apiFetch('', 'POST', {
        householdId,
        name: newPetName.trim(),
        species: newPetSpecies,
        breed: newPetBreed.trim() || undefined,
        birthdateOrAge: newPetAge.trim() || undefined,
      });
      setPets(prev => [...prev, res.data]);
      setShowAddPet(false);
      setNewPetName(''); setNewPetSpecies('dog'); setNewPetBreed(''); setNewPetAge('');
    } catch (e: any) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePet = async (e: React.MouseEvent, pet: ExtendedPet) => {
    e.stopPropagation();
    if (!householdId || !window.confirm(t('pets.confirm_delete', 'Delete {{name}}?', { name: pet.name }))) return;
    try {
      await apiFetch(`/${pet.id}?householdId=${householdId}`, 'DELETE');
      setPets(prev => prev.filter(p => p.id !== pet.id));
      if (selectedPet?.id === pet.id) setSelectedPet(null);
    } catch (e: any) { showToast(e.message, 'error'); }
  };

  // If a pet is selected, show its full profile
  if (selectedPet) {
    return (
      <PetProfile
        pet={selectedPet}
        householdId={householdId!}
        onBack={() => setSelectedPet(null)}
        onUpdate={updated => {
          setPets(prev => prev.map(p => p.id === updated.id ? updated : p));
          setSelectedPet(updated);
        }}
      />
    );
  }

  if (!householdId) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-400">{t('pets.no_household', 'No household selected.')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-page-full mx-auto p-4 md:p-6 space-y-6 pb-32 animate-in fade-in duration-500">

      {/* Header */}
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black tracking-tighter text-slate-900 dark:text-white">{t('pets.title', 'Pets')}</h1>
          <p className="text-2xs font-black text-brand uppercase tracking-[0.3em] mt-0.5">
            {t('pets.subtitle', 'Household Companions')}
          </p>
        </div>
        <button
          onClick={() => setShowAddPet(true)}
          className="bg-brand text-white p-4 rounded-full shadow-xl shadow-brand/20 hover:scale-105 active:scale-95 transition-all"
        >
          <Plus className="w-5 h-5" />
        </button>
      </header>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="md" />
        </div>
      ) : error ? (
        <p className="text-rose-500 text-sm">{error}</p>
      ) : pets.length === 0 ? (
        <EmptyState
          icon={<PawPrint className="w-8 h-8 text-slate-400" />}
          title={t('pets.empty_title', 'No pets yet')}
          description={t('pets.empty_desc', 'Add your first companion to start tracking care, health, and more.')}
        >
          <button
            onClick={() => setShowAddPet(true)}
            className="px-5 py-2.5 bg-brand text-white text-xs font-black uppercase tracking-widest rounded-full hover:bg-brand-hover transition-colors"
          >
            {t('pets.add_first', 'Add First Pet')}
          </button>
        </EmptyState>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {pets.map(pet => (
            <button
              key={pet.id}
              onClick={() => setSelectedPet(pet)}
              className="text-left bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 p-7 hover:shadow-xl hover:border-brand/30 hover:-translate-y-0.5 transition-all group relative"
            >
              {/* Delete */}
              <button
                onClick={e => handleDeletePet(e, pet)}
                className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 p-2 rounded-full text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-all"
              >
                <X className="w-3.5 h-3.5" />
              </button>

              <div className="text-5xl mb-4">{SPECIES_ICONS[pet.species] ?? '🐾'}</div>

              <h3 className="font-black text-xl text-slate-900 dark:text-white tracking-tight">
                {pet.name}
              </h3>

              {(pet.breed || pet.species) && (
                <p className="text-2xs font-black uppercase tracking-widest text-slate-400 mt-1">
                  {[pet.species, pet.breed].filter(Boolean).join(' · ')}
                </p>
              )}

              {pet.birthdateOrAge && (
                <p className="text-xs text-slate-500 mt-1">{pet.birthdateOrAge}</p>
              )}

              {/* Quick stats row */}
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                {pet.weight && (
                  <span className="text-2xs text-slate-400 font-medium">{pet.weight}</span>
                )}
                {pet.vet?.name && (
                  <span className="text-2xs text-slate-400 font-medium truncate">Dr. {pet.vet.name}</span>
                )}
                <ChevronRight className="w-4 h-4 text-brand ml-auto flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Add Pet Modal */}
      {showAddPet && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setShowAddPet(false)} />
          <div className="relative bg-white dark:bg-slate-950 rounded-[2rem] p-8 w-full max-w-md shadow-2xl space-y-4 mx-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black tracking-tight text-slate-900 dark:text-white">{t('pets.add_title', 'Add Pet')}</h2>
              <button onClick={() => setShowAddPet(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            {/* Species picker */}
            <div className="grid grid-cols-5 gap-2">
              {(['dog', 'cat', 'bird', 'reptile', 'other'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => setNewPetSpecies(s)}
                  className={`flex flex-col items-center gap-1 p-3 rounded-2xl border-2 transition-all ${
                    newPetSpecies === s
                      ? 'border-brand bg-brand/5 dark:bg-brand/10'
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                  }`}
                >
                  <span className="text-2xl">{SPECIES_ICONS[s]}</span>
                  <span className="text-2xs font-black uppercase tracking-wider text-slate-500">{t(`pets.species.${s}`, s)}</span>
                </button>
              ))}
            </div>

            <input
              className="w-full bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 ring-brand/40"
              placeholder={t('pets.name_placeholder', 'Pet name *')}
              value={newPetName}
              onChange={e => setNewPetName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddPet()}
            />
            <div className="grid grid-cols-2 gap-3">
              <input
                className="bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 ring-brand/40"
                placeholder={t('pets.breed_placeholder', 'Breed (optional)')}
                value={newPetBreed}
                onChange={e => setNewPetBreed(e.target.value)}
              />
              <input
                className="bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 ring-brand/40"
                placeholder={t('pets.age_placeholder', 'Age / DOB (optional)')}
                value={newPetAge}
                onChange={e => setNewPetAge(e.target.value)}
              />
            </div>

            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setShowAddPet(false)}
                className="flex-1 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleAddPet}
                disabled={!newPetName.trim() || saving}
                className="flex-1 py-3 rounded-xl bg-brand text-white text-sm font-bold hover:bg-brand-hover disabled:opacity-50 transition-colors"
              >
                {saving ? t('common.saving', 'Saving...') : t('pets.add_title', 'Add Pet')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
